import React, { useState } from 'react';
import { Fetcher } from '@this/src/util';
import { Input } from '@this/shared/ui/inputs/input';
import { Button } from '@this/shared/ui/inputs/button';
import { Link } from '@this/shared/ui/navigations/link';
import { Alert } from '@this/shared/ui/feedbacks/alert';
import { Text } from '@this/shared/ui/data_displays/typography';
import type { OneTimePasswordRestartReason } from './one_time_password';
import {
  ONE_TIME_PASSWORD_MESSAGES,
  isOneTimePasswordRestartReason,
  responseErrorCode
} from './one_time_password';

interface Props {
  onRestart: (reason: OneTimePasswordRestartReason | null) => void;
}

const CODE_LENGTH = 6;

const OneTimePasswordForm: React.FC<Props> = ({ onRestart }) => {
  const [code, setCode] = useState<string>('');
  const [errorText, setErrorText] = useState<string | null>(null);
  const [infoText, setInfoText] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isResending, setIsResending] = useState<boolean>(false);

  const handleError = (e: unknown) => {
    const error = responseErrorCode(e);
    if (isOneTimePasswordRestartReason(error)) {
      onRestart(error);
      return;
    }
    setErrorText(
      (error && (ONE_TIME_PASSWORD_MESSAGES[error] || error)) ||
        '通信環境が不安定です。時間をおいてもう一度お試しください。'
    );
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsLoading(true);
    setErrorText(null);
    setInfoText(null);
    try {
      const res = await Fetcher.post<{ redirect?: string }>('/users/auth/one_time_password', { code });
      window.location.href = res.redirect || '/dashboard';
    } catch (error) {
      handleError(error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleResend = async () => {
    if (isResending) return;

    setIsResending(true);
    setErrorText(null);
    setInfoText(null);
    try {
      await Fetcher.post('/users/auth/one_time_password/resend', {});
      setCode('');
      setInfoText('認証コードを再送しました。');
    } catch (error) {
      handleError(error);
    } finally {
      setIsResending(false);
    }
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '16px', width: '100%' }}>
      {errorText && (
        <Alert variant="danger" style={{ width: '100%' }}>
          {errorText}
        </Alert>
      )}
      {infoText && (
        <Alert variant="info" style={{ width: '100%' }}>
          {infoText}
        </Alert>
      )}
      <Text color="description">
        ご登録のメールアドレスに認証コードを送信しました。
        <br />
        メールに記載された6桁の認証コードを入力してください。
      </Text>
      <form
        onSubmit={handleSubmit}
        style={{ display: 'flex', flexDirection: 'column', gap: '16px', width: '100%' }}
      >
        <Input
          type="text"
          placeholder="認証コード"
          name="code"
          value={code}
          onChange={e => setCode(e.target.value.replace(/\D/g, '').slice(0, CODE_LENGTH))}
          inputProps={{ inputMode: 'numeric', autoComplete: 'one-time-code', maxLength: CODE_LENGTH }}
          required
          fullWidth
        />
        <Button type="submit" disabled={code.length !== CODE_LENGTH} loading={isLoading} fullWidth>
          ログイン
        </Button>
      </form>
      <Link size="small" onClick={handleResend}>
        認証コードを再送する
      </Link>
      <Link size="small" onClick={() => onRestart(null)}>
        ログイン画面に戻る
      </Link>
    </div>
  );
};

export default OneTimePasswordForm;
